// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { useDispatch, useSelector } from "react-redux";

// Local Imports
import AutoInvestService from "./auto-invest-.service";
import { formatNumber } from "src/utils/helper/functions";
import CustomSearch from "src/components/custom-search/custom-search";
import CustomTable from "src/components/custom-table/custom-table";
import clsx from "clsx";
import BreadcrumbComponent from "src/components/breadcrumb-component/breadcrumb-component";
import { CREATE_BREADCRUMB } from "src/store/actions";
import { Drawer } from "antd";
import HistoryDrawer from "./components/history-drawer.component";

const DeletedAutoInvest: React.FC = (): ReactElement => {
  let searchTimer = null;
  const autoInvestServiceInstance = new AutoInvestService();
  const dispatch = useDispatch();
  const loadingState = useSelector((state: any) => state?.loading);
  const [autoInvestList, setAutoInvestList] = useState<any[]>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [searchWord, setSearchWord] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [openHistoryDrawer, setOpenHistoryDrawer] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: "Auto Invest",
            type: "link",
            link: "/auto-invest",
          },
        ],
      },
    });
  }, []);

  useEffect(() => {
    fetchListData();
  }, [searchWord, sortField, sortOrder]);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Portfolio#",
      dataIndex: "portfolioCode",
      key: "portfolioCode",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <span
          className="cursor-pointer flex gap-2 items-center"
          onClick={() => {
            setSelectedRecord(record);
            setOpenHistoryDrawer(true);
          }}
        >
          <img src="/images/add-square.svg" alt="add" title="add" />
          <span className="underline">{record?.portfolio?.accountCode}</span>
        </span>
      ),
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      fixed: "left",
      render: (_, record) => <span>{record?.account?.activeKyc?.nameEn}</span>,
    },
    {
      title: "Mutual fund",
      dataIndex: "mutualFund",
      key: "mutualFund",
      render: (_, record) => <span>{record?.mutualFund?.NAME}</span>,
    },
    {
      title: "Investment amount",
      dataIndex: "subscriptionAmount",
      key: "subscriptionAmount",
      sorter: true,
      render: (_, record) => (
        <span>
          {formatNumber(record?.subscriptionAmount)}{" "}
          {record?.subscriptionCurrency}
        </span>
      ),
    },
    {
      title: "Cycle",
      dataIndex: "cycle",
      key: "cycle",
      render: (_, record) =>
        record?.repeatEvery ? (
          <span>
            Every {record?.repeatEvery} {record?.repeatCycle}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Occurance Date",
      dataIndex: "occuranceDate",
      key: "occuranceDate",
      sorter: true,
      render: (_, record) => (
        <span>
          {moment(record?.occuranceDate).format("DD MMM, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Last execution date",
      dataIndex: "lastExecutionDate",
      key: "lastExecutionDate",
      render: (_, record) =>
        record?.lastExecutionDate ? (
          <span>
            {moment(record?.lastExecutionDate).format("DD MMM, YYYY hh:mm A")}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Next execution date",
      dataIndex: "nextExecutionDate",
      key: "nextExecutionDate",
      render: (_, record) =>
        record?.nextExecutionDate ? (
          <span>
            {moment(record?.nextExecutionDate).format("DD MMM, YYYY hh:mm A")}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      render: (text) => (
        <span
          className={clsx(
            "rounded bg-gray-600 text-xs font-medium w-fit py-2 px-3",
            text?.toLowerCase() === "active"
              ? "bg-gray-600 text-green-500"
              : "bg-pink-200 text-pink-300"
          )}
        >
          {text?.toLowerCase() === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const fetchListData = async (page = 1): Promise<void> => {
    const data: any = await autoInvestServiceInstance.getAutoInvestList(
      page,
      searchWord,
      sortField,
      sortOrder,
      true
    );

    if (data !== undefined) {
      setResponseDate(data?.data);
      setAutoInvestList(data?.data?.autoInvests);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <section className="flex flex-col">
      <BreadcrumbComponent />
      <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
        <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
          <p className=" text-black-300 text-base font-bold">
            {!loadingState ? autoInvestList?.length : ""} requests
          </p>
          <div className="flex items-center">
            <CustomSearch onChange={(event) => onSearch(event)} />
          </div>
        </div>
        {autoInvestList?.length ? (
          <CustomTable
            columns={columns}
            data={autoInvestList}
            showPagination={true}
            pageSize={responseDate?.pageSize}
            totalCount={responseDate?.totalCount}
            page={responseDate?.currentPage}
            handleTableChange={handleTableChange}
            onPageChange={(page) => {
              fetchListData(page);
            }}
          />
        ) : !loadingState ? (
          <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
            <img
              className="w-[40px] h-[40px]"
              src="/images/no-items.svg"
              alt="no data kamco"
            />
            <p className="text-lg text-gray-500 font-medium">
              There are no items in this list
            </p>
          </div>
        ) : (
          ""
        )}
      </div>

      <Drawer
        title="Basic Drawer"
        open={openHistoryDrawer}
        destroyOnClose={true}
        maskClosable={true}
        onClose={() => setOpenHistoryDrawer(false)}
        width={600}
        className="change-account-drawer"
      >
        <HistoryDrawer
          setOpenHistoryDrawer={(value: boolean) => setOpenHistoryDrawer(value)}
          history={selectedRecord?.autoInvestHistory}
        />
      </Drawer>
    </section>
  );
};

export default DeletedAutoInvest;
